import React, { useMemo, useEffect } from 'react';
import { Box, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { WorkflowPage } from '../../../components';
import { FORM_CONFIGS } from '../../../constants/formConfigurations';
import { TABLE_COLUMNS } from '../../../constants/tableConfigurations';
import { profileLeavesTabStyles } from './ProfileLeavesTab.styles';
import { useAuth, useExpenses, useExpenseTypes } from '../../../hooks';

const ProfileExpensesTab = ({ userId = null }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const targetUserId = userId || user?.id;

  const { expenses, isLoading, isCreating, handleFormSubmit, fetchData } =
    useExpenses(false, userId);
  const { expenseTypes, fetchData: fetchExpenseTypes } = useExpenseTypes();

  useEffect(() => {
    if (targetUserId) {
      fetchData();
      fetchExpenseTypes();
    }
  }, [targetUserId]);

  const formFields = useMemo(
    () =>
      FORM_CONFIGS.expenses.fields.map(field => {
        if (field.name === 'expenseType') {
          return {
            ...field,
            options: expenseTypes
              .filter(et => et.expense_type_status)
              .map(et => ({
                value: et.id,
                labelKey: et.expense_type_name,
              })),
          };
        }
        return field;
      }),
    [expenseTypes]
  );

  const columns = useMemo(
    () =>
      TABLE_COLUMNS.expenses.map(col =>
        col.field === 'status'
          ? {
              ...col,
              render: (value, row) => {
                if (value === 'REJECTED') {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('expenses.status.rejected')}
                        color="error"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('REJECTED')}
                      />
                    </Box>
                  );
                }
                if (value === 'APPROVED') {
                  return (
                    <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                      <Chip
                        label={t('expenses.status.approved')}
                        color="success"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('APPROVED')}
                      />
                    </Box>
                  );
                }
                return (
                  <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                    <Chip
                      label={t(`expenses.status.${value?.toLowerCase?.()}`)}
                      sx={profileLeavesTabStyles.statusChip(value)}
                      size="small"
                      clickable={false}
                      component="span"
                      tabIndex={-1}
                      onKeyDown={e => e.preventDefault()}
                      onClick={e => e.preventDefault()}
                    />
                  </Box>
                );
              },
            }
          : col
      ),
    [t]
  );

  const handleAddExpense = async data => {
    return await handleFormSubmit(data);
  };

  const statusCounts = useMemo(() => {
    if (!Array.isArray(expenses))
      return {
        all: 0,
        PENDING: 0,
        APPROVED: 0,
        REJECTED: 0,
      };
    const counts = {
      all: expenses.length,
      PENDING: 0,
      APPROVED: 0,
      REJECTED: 0,
    };
    expenses.forEach(e => {
      if (counts.hasOwnProperty(e.status)) {
        counts[e.status]++;
      }
    });
    return counts;
  }, [expenses]);

  const pageConfig = useMemo(() => ({
    columns,
    filterConfigs: [],
    searchConfig: { fields: ['reason'], placeholder: 'Masraf arama...' },
    formConfig: { 
      fields: formFields,
      title: FORM_CONFIGS.expenses.title
    },
  }), [columns, formFields]);

  return (
    <WorkflowPage
      entityType="expenses"
      data={expenses}
      loading={isLoading}
      columns={pageConfig.columns}
      formFields={pageConfig.formConfig.fields}
      filterConfigs={pageConfig.filterConfigs}
      searchConfig={pageConfig.searchConfig}
      formConfig={pageConfig.formConfig}
      statusCounts={statusCounts}
      onFormSubmit={handleAddExpense}
      onApprove={null}
      onReject={null}
      rowActions={[]}
      canAdd={true}
      addButtonText={t('expenses.actions.add')}
      addButtonIcon={<AddIcon />}
      currentPage={t('profile.tabs.expenses')}
      showViewGuard={false}
    />
  );
};

export default ProfileExpensesTab;
